import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import ProductGrid from '../components/ProductGrid';
import QuickOrderModal from '../components/QuickOrderModal';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { getProducts, getCategories, getTags, Product, Category as CategoryType, Tag } from '../api/products';

type SortOption = 'default' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';

const Category: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';

    const [allProducts, setAllProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Filter states
    const [priceRange, setPriceRange] = useState<[number, number]>([0, 500]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [sortBy, setSortBy] = useState<SortOption>('default');
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

    const [tags, setTags] = useState<Tag[]>([]);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    const [categories, setCategories] = useState<CategoryType[]>([]);

    // Scroll to top when page changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentPage]);

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategories, selectedTags, priceRange, sortBy, slug, searchQuery]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const data = await getCategories();
                setCategories(data);
            } catch (error) {
                console.error('Failed to fetch categories', error);
            }
        };
        const fetchTags = async () => {
            try {
                const data = await getTags();
                setTags(data);
            } catch (error) {
                console.error('Failed to fetch tags', error);
            }
        };
        fetchCategories();
        fetchTags();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const data = await getProducts({ category: slug === 'all' ? undefined : slug });
                setAllProducts(data);
                setFilteredProducts(data);

                setAllProducts(data);
                setFilteredProducts(data);
            } catch (error) {
                console.error('Failed to fetch products', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, [slug]);

    useEffect(() => {
        let result = [...allProducts];

        // Apply category filter
        if (selectedCategories.length > 0) {
            result = result.filter(p =>
                p.categories?.some(c => selectedCategories.includes(c.slug)) ||
                selectedCategories.includes(p.category?.slug || '')
            );
        }

        // Apply tag filter
        if (selectedTags.length > 0) {
            result = result.filter(p =>
                p.tags?.some(t => selectedTags.includes(t.slug))
            );
        }

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(p =>
                p.title_fr.toLowerCase().includes(query) ||
                p.title_ar.includes(query)
            );
        }

        // Apply price filter
        result = result.filter(p => {
            const price = p.discount_price || p.price;
            return price >= priceRange[0] && price <= priceRange[1];
        });

        // Apply sorting
        switch (sortBy) {
            case 'price-asc':
                result.sort((a, b) => (a.discount_price || a.price) - (b.discount_price || b.price));
                break;
            case 'price-desc':
                result.sort((a, b) => (b.discount_price || b.price) - (a.discount_price || a.price));
                break;
            case 'name-asc':
                result.sort((a, b) => {
                    const nameA = isRTL ? a.title_ar : a.title_fr;
                    const nameB = isRTL ? b.title_ar : b.title_fr;
                    return nameA.localeCompare(nameB);
                });
                break;
            case 'name-desc':
                result.sort((a, b) => {
                    const nameA = isRTL ? a.title_ar : a.title_fr;
                    const nameB = isRTL ? b.title_ar : b.title_fr;
                    return nameB.localeCompare(nameA);
                });
                break;
        }

        setFilteredProducts(result);
    }, [allProducts, selectedCategories, selectedTags, priceRange, sortBy, isRTL, searchQuery]);

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const paginatedProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const openQuickOrder = (product: Product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
    };

    const getCategoryTitle = () => {
        if (slug === 'all' || !slug) return t('common.products');
        // Try to find the category name from the fetched categories
        const category = categories.find(c => c.slug === slug);
        if (category) {
            return isRTL ? category.name_ar : category.name_fr;
        }
        return slug.charAt(0).toUpperCase() + slug.slice(1);
    };

    const toggleCategory = (categorySlug: string) => {
        setSelectedCategories(prev =>
            prev.includes(categorySlug)
                ? prev.filter(c => c !== categorySlug)
                : [...prev, categorySlug]
        );
    };

    const clearFilters = () => {
        setSelectedCategories([]);
        setSelectedTags([]);
        setPriceRange([0, 500]);
        setSortBy('default');
        setSearchQuery('');
    };

    const toggleTag = (tagSlug: string) => {
        setSelectedTags(prev =>
            prev.includes(tagSlug)
                ? prev.filter(t => t !== tagSlug)
                : [...prev, tagSlug]
        );
    };

    return (
        <div className="bg-light min-h-screen">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl md:text-5xl font-bold text-dark mb-4">
                        {getCategoryTitle()}
                    </h1>
                    <p className="text-lg text-gray-600">
                        {filteredProducts.length} {t('category.productsFound')}
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Filters */}
                    <motion.div
                        className={`lg:w-80 ${showFilters ? 'block' : 'hidden lg:block'}`}
                        initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="bg-white rounded-3xl shadow-card p-6 sticky top-24">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-dark">{t('category.filters')}</h3>
                                <button
                                    onClick={clearFilters}
                                    className="text-sm text-primary hover:text-primary-dark font-medium"
                                >
                                    {t('category.clearAll')}
                                </button>
                            </div>

                            {/* Search Filter */}
                            <div className="mb-6 pb-6 border-b border-gray-200">
                                <h4 className="font-bold text-dark mb-4">{t('category.search')}</h4>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder={t('category.searchPlaceholder') || 'Search...'}
                                        className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'
                                            }`}
                                    />
                                    <svg
                                        className={`w-5 h-5 text-gray-400 absolute top-1/2 transform -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'}`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Sort By Filter */}
                            <div className="mb-6 pb-6 border-b border-gray-200">
                                <h4 className="font-bold text-dark mb-4">{t('category.sortBy')}</h4>
                                <div className="space-y-2">
                                    {[
                                        { value: 'default', label: t('category.sortDefault') },
                                        { value: 'price-asc', label: t('category.sortPriceAsc') },
                                        { value: 'price-desc', label: t('category.sortPriceDesc') },
                                        { value: 'name-asc', label: t('category.sortNameAsc') },
                                        { value: 'name-desc', label: t('category.sortNameDesc') },
                                    ].map((option) => (
                                        <label key={option.value} className="flex items-center gap-3 cursor-pointer group">
                                            <input
                                                type="radio"
                                                name="sortBy"
                                                value={option.value}
                                                checked={sortBy === option.value}
                                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                                                className="w-5 h-5 border-gray-300 text-primary focus:ring-primary"
                                            />
                                            <span className="text-gray-700 group-hover:text-primary transition-smooth">
                                                {option.label}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Price Range Filter */}
                            <div className="mb-6 pb-6 border-b border-gray-200">
                                <h4 className="font-bold text-dark mb-4">{t('category.priceRange')}</h4>
                                <div className="space-y-4">
                                    <input
                                        type="range"
                                        min="0"
                                        max="500"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full accent-primary"
                                    />
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-medium text-gray-600">0 MAD</span>
                                        <span className="font-bold text-primary">{priceRange[1]} MAD</span>
                                    </div>
                                </div>
                            </div>

                            {/* Category Filter */}
                            {slug === 'all' && categories.length > 0 && (
                                <div className="mb-6 pb-6 border-b border-gray-200">
                                    <h4 className="font-bold text-dark mb-4">{t('category.categories')}</h4>
                                    <div className="space-y-2">
                                        {categories.map(cat => (
                                            <label key={cat.slug} className="flex items-center gap-3 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCategories.includes(cat.slug)}
                                                    onChange={() => toggleCategory(cat.slug)}
                                                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <span className="text-gray-700 group-hover:text-primary transition-smooth capitalize">
                                                    {isRTL ? cat.name_ar : cat.name_fr}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tag Filter */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <h4 className="font-bold text-dark mb-4">{t('category.tags')}</h4>
                                <div className="space-y-2">
                                    {tags.length > 0 ? (
                                        tags.map(tag => (
                                            <label key={tag.slug} className="flex items-center gap-3 cursor-pointer group">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTags.includes(tag.slug)}
                                                    onChange={() => toggleTag(tag.slug)}
                                                    className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                                <span className="text-gray-700 group-hover:text-primary transition-smooth capitalize">
                                                    {isRTL ? tag.name_ar : tag.name_fr}
                                                </span>
                                            </label>
                                        ))
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">
                                            {isRTL ? 'لا توجد وسوم متوفرة' : 'No tags available'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <div className="flex-1">
                        {/* Toolbar */}
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6">
                            {/* Mobile Filters Button */}
                            <div className="lg:hidden w-full sm:w-auto">
                                <button
                                    onClick={() => setShowFilters(!showFilters)}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl shadow-soft text-dark font-medium hover:bg-gray-50 transition-smooth"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                    </svg>
                                    {showFilters ? t('common.close') : t('category.filters')}
                                </button>
                            </div>

                            {/* View Toggle */}
                            <div className="flex items-center gap-2 ml-auto">
                                <span className="text-sm font-medium text-gray-500 hidden sm:block">{t('common.view') || 'View'}:</span>
                                <div className="bg-white rounded-lg shadow-sm p-1 flex border border-gray-100">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'grid'
                                            ? 'bg-primary text-white shadow-sm'
                                            : 'text-gray-400 hover:text-primary hover:bg-gray-50'}`}
                                        title="Grid View"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-2 rounded-md transition-all duration-200 ${viewMode === 'list'
                                            ? 'bg-primary text-white shadow-sm'
                                            : 'text-gray-400 hover:text-primary hover:bg-gray-50'}`}
                                        title="List View"
                                    >
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Products */}
                        {loading ? (
                            <LoadingSkeleton count={8} gridClassName="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" />
                        ) : filteredProducts.length === 0 ? (
                            <div className="bg-white rounded-3xl shadow-card p-12 text-center">
                                <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                                <h3 className="text-2xl font-bold text-gray-700 mb-2">{t('category.noProducts')}</h3>
                                <p className="text-gray-500 mb-6">{t('category.tryAdjustingFilters')}</p>
                                <button
                                    onClick={clearFilters}
                                    className="px-6 py-3 bg-primary text-white rounded-full font-bold hover:bg-primary-dark transition-smooth"
                                >
                                    {t('category.clearFilters')}
                                </button>
                            </div>
                        ) : (
                            <>
                                <ProductGrid
                                    products={paginatedProducts}
                                    loading={false}
                                    onQuickOrder={openQuickOrder}
                                    viewMode={viewMode}
                                    gridClassName={viewMode === 'grid'
                                        ? "grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
                                        : "grid grid-cols-1 gap-4 md:gap-6"}
                                />

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="mt-12 flex justify-center items-center gap-2">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className={`p-2 rounded-lg border ${currentPage === 1
                                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-primary hover:border-primary'
                                                } transition-smooth`}
                                        >
                                            <svg className={`w-5 h-5 ${isRTL ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>

                                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                className={`w-10 h-10 rounded-lg font-medium transition-smooth ${currentPage === page
                                                    ? 'bg-primary text-white shadow-md'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        ))}

                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className={`p-2 rounded-lg border ${currentPage === totalPages
                                                ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                                                : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:text-primary hover:border-primary'
                                                } transition-smooth`}
                                        >
                                            <svg className={`w-5 h-5 ${isRTL ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <QuickOrderModal
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                product={selectedProduct}
            />
        </div>
    );
};

export default Category;
